import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  PaperPlaneTilt, CalendarPlus, MagicWand, Sparkle, LinkSimple, X, BellRinging, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { relTime } from "@/lib/format";

const ICONS = {
  email_sent: { icon: PaperPlaneTilt, tint: "bg-green-100 text-green-700" },
  event_created: { icon: CalendarPlus, tint: "bg-indigo-100 text-indigo-700" },
  triage: { icon: MagicWand, tint: "bg-violet-100 text-violet-700" },
  briefing: { icon: Sparkle, tint: "bg-amber-100 text-amber-700" },
  connect: { icon: LinkSimple, tint: "bg-slate-100 text-slate-700" },
  dismissed: { icon: X, tint: "bg-rose-100 text-rose-700" },
  notification: { icon: BellRinging, tint: "bg-sky-100 text-sky-700" },
};

export default function ActivityLog() {
  const { data: logs, isLoading } = useQuery({ queryKey: ["activity"], queryFn: api.actionLog });

  return (
    <div className="max-w-2xl mx-auto px-6 sm:px-10 py-10">
      <PageHeader testid="activity-header" eyebrow="Audit trail" title="Activity Log" subtitle="Everything Auppo has done, in order." />

      {isLoading ? (
        <div className="text-center py-16 text-slate-400 aria-thinking">Loading…</div>
      ) : (logs?.length || 0) === 0 ? (
        <div className="text-center py-20 text-slate-400" data-testid="activity-empty">
          <ClockCounterClockwise size={40} className="mx-auto mb-3 opacity-50" />
          No activity yet.
        </div>
      ) : (
        <div className="relative pl-2">
          <div className="absolute left-[19px] top-2 bottom-2 w-px bg-border" />
          <div className="space-y-5">
            {logs.map((l, i) => {
              const cfg = ICONS[l.type] || { icon: Sparkle, tint: "bg-slate-100 text-slate-700" };
              const Icon = cfg.icon;
              return (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                  data-testid={`log-${l.id}`}
                  className="relative flex gap-4"
                >
                  <div className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center z-10 ${cfg.tint}`}>
                    <Icon weight="fill" size={16} />
                  </div>
                  <div className="bg-white border border-border rounded-xl px-4 py-3 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-800">{l.summary}</span>
                      <span className="text-xs text-slate-400 shrink-0">{relTime(l.created_at)}</span>
                    </div>
                    {l.detail && <div className="text-sm text-slate-500 mt-0.5">{l.detail}</div>}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
